import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation  } from 'react-router-dom';
import { getProduct } from "../api/productApi";
import styles from './ProductDetailPage.module.css';
import { addToCart } from "../api/cartApi";
import { isLoggedIn } from "../utils/tokenUtils";
import { useCart } from "../context/CartContext";
import { toggleWishlist, getWishlistStatus } from '../api/wishlistApi';
import { addRecentProduct } from "../utils/recentProducts";
import { getReviewsByProduct, createReview, deleteReview } from '../api/reviewApi';
import { getSettings } from '../api/settingsApi';
import { useModal } from '../context/ModalContext';
import axios from 'axios';
import FallbackImage from '../components/FallbackImage';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function ProductDetailPage() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const { refreshCartCount, openCartSidebar } = useCart();
    const [wished, setWished] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [avgRating, setAvgRating] = useState(0);
    const [reviewForm, setReviewForm] = useState({ rating: 5, content: '', imageFile: null });
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [myOrders, setMyOrders] = useState([]);
    const [defaultDeliveryFee, setDefaultDeliveryFee] = useState(0);
    const [freeDeliveryMin, setFreeDeliveryMin] = useState(0);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const { showAlert, showConfirm } = useModal();
    const location = useLocation();

    useEffect(() => {
        const init = async () => {
            const loadProduct = async () => {
                try {
                    const response = await getProduct(productId);
                    setProduct(response.data);
                    // 동적 OG 태그 설정
                    const data = response.data;
                    document.title = `${data.name} - 종합센터몰`;

                    const setMeta = (property, content) => {
                        let el = document.querySelector(`meta[property='${property}']`);
                        if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
                        el.setAttribute('content', content);
                    };

                    setMeta('og:title', `${data.name} - 종합센터몰`);
                    setMeta('og:description', data.description || '종합센터몰에서 만나보세요.');
                    setMeta('og:image', `${API_URL}${data.imageUrl}`);
                    setMeta('og:url', window.location.href);
                    addRecentProduct({
                        productId: response.data.productId,
                        name: response.data.name,
                        price: response.data.price,
                        originalPrice: response.data.originalPrice,
                        discountRate: response.data.discountRate,
                        imageUrl: response.data.imageUrl,
                    });
                    // 관련 상품 로드
                    if (response.data.categoryId) {
                        try {
                            const relatedRes = await axios.get(
                                `${API_URL}/api/products?categoryId=${response.data.categoryId}&size=5`
                            );
                            setRelatedProducts(
                                relatedRes.data.products.filter(p => p.productId !== response.data.productId).slice(0, 4)
                            );
                        } catch {}
                    }
                } catch (err) {
                    console.error('상품 로딩 실패', err);
                    navigate('/products');
                } finally {
                    setLoading(false);
                }
            };

            const loadReviews = async () => {
                try {
                    const res = await getReviewsByProduct(productId);
                    setReviews(res.data);
                    if (res.data.length > 0) {
                        const avg = res.data.reduce((sum, r) => sum + r.rating, 0) / res.data.length;
                        setAvgRating(Math.round(avg * 10) / 10);
                    }
                } catch {}
            };

            if (isLoggedIn()) {
                getWishlistStatus(productId)
                    .then(res => setWished(res.data.wished))
                    .catch(() => {});
                const { getOrders } = await import('../api/orderApi');
                getOrders().then(res => setMyOrders(res.data)).catch(() => {});
            }

            await loadProduct();
            await loadReviews();

            getSettings().then(res => {
                setDefaultDeliveryFee(parseInt(res.data.delivery_fee || '0'));
                setFreeDeliveryMin(parseInt(res.data.free_delivery_min || '0'));
            }).catch(() => {});
        };
        init();
        return () => {
            document.title = '종합센터몰';
        };
    }, [productId]);

    const handleAddToCart = async () => {
        if (!isLoggedIn()) { navigate('/login', { state: { from: location.pathname } }); return; }
        if (product.options?.length > 0 && !selectedOption) { await showAlert('옵션을 선택해주세요.', 'warning'); return; }
        try {
            await addToCart(product.productId, quantity, selectedOption?.optionId || null);
            openCartSidebar();
        } catch { await showAlert('장바구니 추가에 실패했습니다.', 'error'); }
    };

    const handleBuyNow = async () => {
        if (!isLoggedIn()) { navigate('/login', { state: { from: location.pathname } }); return; }
        if (product.options?.length > 0 && !selectedOption) { await showAlert('옵션을 선택해주세요.', 'warning'); return; }
        try {
            await addToCart(product.productId, quantity, selectedOption?.optionId || null);
            refreshCartCount();
            navigate('/order');
        } catch { await showAlert('장바구니 추가에 실패했습니다.', 'error'); }
    };

    const handleReviewSubmit = async () => {
        if (!reviewForm.content.trim()) { await showAlert('리뷰 내용을 입력해 주세요.', 'warning'); return; }
        const purchasedOrder = myOrders.find(o =>
            o.orderItems?.some(item => item.productId === Number(productId))
        );
        if (!purchasedOrder) { await showAlert('구매한 상품에만 리뷰를 작성할 수 있습니다.', 'warning'); return; }

        const formData = new FormData();
        formData.append('productId', productId);
        formData.append('orderId', purchasedOrder.orderId);
        formData.append('rating', reviewForm.rating);
        formData.append('content', reviewForm.content);
        if (reviewForm.imageFile) formData.append('imageFile', reviewForm.imageFile);

        try {
            await createReview(formData);
            await showAlert('리뷰가 등록되었습니다.', 'success');
            setReviewForm({ rating: 5, content: '', imageFile: null });
            setShowReviewForm(false);
            const res = await getReviewsByProduct(productId);
            setReviews(res.data);
            const avg = res.data.reduce((sum, r) => sum + r.rating, 0) / res.data.length;
            setAvgRating(Math.round(avg * 10) / 10);
        } catch { await showAlert('리뷰 등록에 실패했습니다.', 'error'); }
    };

    const handleReviewDelete = async (reviewId) => {
        const ok = await showConfirm('리뷰를 삭제하시겠습니까?');
        if (!ok) return;
        try {
            await deleteReview(reviewId);
            setReviews(prev => prev.filter(r => r.reviewId !== reviewId));
        } catch { await showAlert('삭제에 실패했습니다.', 'error'); }
    };

    const images = [];
    if (product?.imageUrl) images.push(product.imageUrl);
    if (product?.images?.length > 0) {
        product.images.forEach(img => {
            if (!images.includes(img.imageUrl)) images.push(img.imageUrl);
        });
    }

    const basePrice = product?.price || 0;
    const optionPrice = selectedOption?.additionalPrice || 0;
    const finalPrice = (basePrice + optionPrice) * quantity;

    if (loading) return <p className={styles.loading}>로딩 중...</p>;
    if (!product) return null;

    const optionGroups = {};
    product.options?.forEach(opt => {
        if (!optionGroups[opt.optionName]) optionGroups[opt.optionName] = [];
        optionGroups[opt.optionName].push(opt);
    });

    return (
        <div className={styles.container}>
            <button className={styles.backButton} onClick={() => navigate('/products')}>
                ← 목록으로
            </button>

            <div className={styles.productDetail}>
                <div className={styles.imageSection}>
                    <div className={styles.mainImageBox}>
                        <FallbackImage
                            src={`${API_URL}${images[currentImageIndex]}`}
                            alt={product.name}
                            className={styles.mainImage}
                        />
                        {images.length > 1 && (
                            <>
                                <button className={styles.prevBtn}
                                    onClick={() => setCurrentImageIndex(i => Math.max(0, i - 1))}>‹</button>
                                <button className={styles.nextBtn}
                                    onClick={() => setCurrentImageIndex(i => Math.min(images.length - 1, i + 1))}>›</button>
                            </>
                        )}
                    </div>
                    {images.length > 1 && (
                        <div className={styles.thumbnails}>
                            {images.map((img, i) => (
                                <img key={i}
                                    src={`${API_URL}${img}`}
                                    alt={`${i + 1}`}
                                    className={`${styles.thumbnail} ${currentImageIndex === i ? styles.activeThumbnail : ''}`}
                                    onClick={() => setCurrentImageIndex(i)} />
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.infoBox}>
                    <p className={styles.categoryName}>{product.categoryName}</p>
                    <h2 className={styles.productName}>{product.name}</h2>

                    {reviews.length > 0 && (
                        <div className={styles.ratingBar}>
                            <span className={styles.ratingStars}>♥ {avgRating}</span>
                            <span className={styles.ratingCount}>리뷰 {reviews.length}</span>
                        </div>
                    )}

                    <div className={styles.priceBox}>
                        {product.discountRate > 0 && (
                            <span className={styles.discountRate}>{product.discountRate}%</span>
                        )}
                        <span className={styles.price}>{basePrice.toLocaleString()}원</span>
                        {product.originalPrice && (
                            <span className={styles.originalPrice}>{product.originalPrice.toLocaleString()}원</span>
                        )}
                    </div>
                    <hr className={styles.divider} />
                    

                    {Object.keys(optionGroups).length > 0 && (
                        <div className={styles.optionSection}>
                            {Object.entries(optionGroups).map(([groupName, opts]) => (
                                <div key={groupName} className={styles.optionGroup}>
                                    <p className={styles.optionLabel}>{groupName}</p>
                                    <div className={styles.optionButtons}>
                                        {opts.map(opt => (
                                            <button key={opt.optionId}
                                                className={`${styles.optionBtn} ${selectedOption?.optionId === opt.optionId ? styles.selectedOption : ''} ${opt.stock === 0 ? styles.soldOutOption : ''}`}
                                                onClick={() => opt.stock > 0 && setSelectedOption(opt)}
                                                disabled={opt.stock === 0}>
                                                {opt.optionValue}
                                                {opt.additionalPrice > 0 && ` (+${opt.additionalPrice.toLocaleString()}원)`}
                                                {opt.stock === 0 && ' (품절)'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className={styles.quantitySection}>
                        <p className={styles.optionLabel}>수량</p>
                        <div className={styles.quantityControl}>
                            <button onClick={() => setQuantity(q => Math.max(1, q - 1))}>-</button>
                            <span>{quantity}</span>
                            <button onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}>+</button>
                        </div>
                    </div>

                    <div className={styles.totalPrice}>
                        <span>총 가격</span>
                        <span className={styles.totalPriceValue}>{finalPrice.toLocaleString()}원</span>
                    </div>
                    <div className={styles.deliveryFeeInfo}>
                        <span className={styles.deliveryFeeLabel}>배송비</span>
                        <span className={styles.deliveryFeeValue}>
                            {(() => {
                                const fee = product.deliveryFee != null ? product.deliveryFee : defaultDeliveryFee;
                                if (fee === 0) return '무료';
                                const freeMsg = freeDeliveryMin > 0 ? ` (${freeDeliveryMin.toLocaleString()}원 이상 무료)` : '';
                                return `${fee.toLocaleString()}원${freeMsg}`;
                            })()}
                        </span>
                    </div>

                    <div className={styles.buttonGroup}>
                        <button className={styles.cartButton} onClick={handleAddToCart}>장바구니</button>
                        <button className={styles.buyButton} onClick={handleBuyNow}>바로 구매</button>
                        <button
                            onClick={async () => {
                                if (!isLoggedIn()) { await showAlert('로그인이 필요합니다.', 'info'); return; }
                                try {
                                    const res = await toggleWishlist(productId);
                                    setWished(res.data.wished);
                                } catch { await showAlert('오류가 발생했습니다.', 'error'); }
                            }}
                            style={{
                                padding: '14px 24px',
                                background: wished ? '#000' : 'white',
                                color: wished ? 'white' : '#000',
                                border: '1px solid #000',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '15px',
                                transition: 'all 0.2s'
                            }}
                        >
                            {wished ? '♥ 찜 완료' : '♡ 찜하기'}
                        </button>
                    </div>
                </div>
            </div>

            <div className={styles.detailSection}>
                <div className={styles.detailContent}>
                    <div className={styles.infoBlock}>
                        <h4>상품 상세</h4>
                        {product.description ? (
                            <div className={styles.richContent} dangerouslySetInnerHTML={{ __html: product.description }} />
                        ) : (
                            <p className={styles.emptyText}>등록된 상품 상세 정보가 없습니다.</p>
                        )}
                    </div>
                    <div className={styles.infoBlock}>
                        <h4>교환/반품 안내</h4>
                        <ul>
                            <li>교환/반품 신청은 수령 후 7일 이내 가능합니다.</li>
                            <li>단순 변심의 경우 왕복 배송비 고객 부담입니다.</li>
                            <li>상품 불량/오배송의 경우 배송비 무료입니다.</li>
                            <li>개봉 또는 사용 후에는 교환/반품이 불가합니다.</li>
                            <li>상품 훼손 및 포장 파손의 경우 교환/반품이 제한될 수 있습니다.</li>
                        </ul>
                    </div>

                    <div className={styles.infoBlock}>
                        <div className={styles.reviewHeader}>
                            <h4>리뷰 ({reviews.length})</h4>
                            {reviews.length > 0 && (
                                <span className={styles.avgRating}>♥ {avgRating}</span>
                            )}
                            {isLoggedIn() && (
                                <button className={styles.writeReviewBtn}
                                    onClick={() => setShowReviewForm(prev => !prev)}>
                                    {showReviewForm ? '취소' : '리뷰 작성'}
                                </button>
                            )}
                        </div>

                        {showReviewForm && (
                            <div className={styles.reviewForm}>
                                <div className={styles.ratingSelect}>
                                    <span>별점</span>
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <button key={star}
                                            className={`${styles.starBtn} ${reviewForm.rating === star ? styles.starBtnActive : ''}`}
                                            onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}>
                                            {star}
                                        </button>
                                    ))}
                                    <span className={styles.selectedRating}>♥ {reviewForm.rating}.0</span>
                                </div>
                                <textarea
                                    placeholder="리뷰 내용을 입력해 주세요."
                                    value={reviewForm.content}
                                    onChange={e => setReviewForm(prev => ({ ...prev, content: e.target.value }))}
                                    className={styles.reviewTextarea}
                                />
                                <input type="file" accept="image/*"
                                    onChange={e => setReviewForm(prev => ({ ...prev, imageFile: e.target.files[0] }))}
                                    className={styles.reviewImageInput}
                                />
                                <button className={styles.submitReviewBtn} onClick={handleReviewSubmit}>등록</button>
                            </div>
                        )}

                        {reviews.length === 0 ? (
                            <p className={styles.emptyText}>아직 리뷰가 없습니다.</p>
                        ) : (
                            <div className={styles.reviewList}>
                                {reviews.map(review => (
                                    <div key={review.reviewId} className={styles.reviewItem}>
                                        <div className={styles.reviewTop}>
                                            <span className={styles.reviewUser}>{review.userName}</span>
                                            <span className={styles.reviewRating}>♥ {review.rating}.0</span>
                                            <span className={styles.reviewDate}>
                                                {new Date(review.createdAt).toLocaleDateString()}
                                            </span>
                                            {isLoggedIn() && (
                                                <button className={styles.deleteReviewBtn}
                                                    onClick={() => handleReviewDelete(review.reviewId)}>
                                                    삭제
                                                </button>
                                            )}
                                        </div>
                                        <p className={styles.reviewContent}>{review.content}</p>
                                        {review.imageUrl && (
                                            <img src={`${API_URL}${review.imageUrl}`}
                                                alt="리뷰 이미지" className={styles.reviewImage} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 관련 상품 */}
            {relatedProducts.length > 0 && (
                <div className={styles.relatedSection}>
                    <h3 className={styles.relatedTitle}>관련 상품</h3>
                    <div className={styles.relatedGrid}>
                        {relatedProducts.map(p => (
                            <div key={p.productId} className={styles.relatedCard}
                                onClick={() => { navigate(`/products/${p.productId}`); window.scrollTo(0, 0); }}>
                                <div className={styles.relatedImageBox}>
                                    {p.discountRate > 0 && (
                                        <span className={styles.relatedBadge}>{p.discountRate}%</span>
                                    )}
                                    {p.imageUrl ? (
                                        <img src={`${API_URL}${p.imageUrl}`}
                                            alt={p.name} className={styles.relatedImage} />
                                    ) : (
                                        <div className={styles.noImage}>이미지 없음</div>
                                    )}
                                </div>
                                <div className={styles.relatedInfo}>
                                    <p className={styles.relatedName}>{p.name}</p>
                                    <p className={styles.relatedPrice}>{p.price?.toLocaleString()}원</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProductDetailPage;